import type { ModalProps } from "../../../../../shared/frontend/ui";
import { Modal,theme } from "../../../../../shared/frontend/ui";
import React from "react";
import { LoadingOverlay } from "./LoadingOverlay";
import { useAutoFocusInContainer } from "./hooks/useAutoFocusInContainer";
import { useSubmitLock } from "../../../../../shared/frontend/components/hooks/useSubmitLock";

export type ThemedModalProps = ModalProps & {
  /**
   * Wenn gesetzt: OK-Button wird zu htmlType="submit" form={formId}. Enter im Form-Body
   * triggert dann nativ den OK-Klick. Die innere Form muss id={formId} + onFinish setzen.
   */
  formId?: string;
  /** Spiegelt confirmLoading semantisch — kommuniziert "Submit läuft". */
  submitting?: boolean;
  /**
   * Der **Inhalt** des Modals lädt noch. Bewusst nicht `loading` genannt — `confirmLoading`
   * und `submitting` bedeuten hier bereits „Speichern läuft".
   *
   * Wirkung: Body gedimmt und gesperrt (`LoadingOverlay scope="block"`), OK deaktiviert,
   * AutoFocus wartet auf den fertigen Inhalt.
   *
   * Binden an den **Erstaufbau** (`isPending`), nie an `isFetching` — sonst sperrt jedes
   * Hintergrund-Neuladen das offene Modal.
   */
  contentLoading?: boolean;
  /** Default true (außer bei okType="danger"): erstes interaktives Feld bekommt Fokus. */
  autoFocusFirstField?: boolean;
  /** Default true: Enter im Body triggert OK über den nativen Form-Submit. */
  submitOnEnter?: boolean;
};

/**
 * Modal mit Theme-Tokens + standardisierter Tastatur-UX:
 *  - Escape schließt (Antd-Default, nicht überschrieben).
 *  - Enter im Form-Body submittet, wenn formId gesetzt.
 *  - AutoFocus auf erstes Eingabefeld nach Open.
 */
export function ThemedModal({
  styles,
  formId,
  submitting,
  contentLoading,
  autoFocusFirstField,
  submitOnEnter = true,
  okButtonProps,
  confirmLoading,
  modalRender,
  open,
  okType,
  maskClosable,
  mask,
  destroyOnClose,
  onOk,
  children,
  ...props
}: ThemedModalProps) {
  const { token } = theme.useToken();
  const elevatedBg = { background: token.colorBgElevated };
  const mergeElevatedStyles = React.useCallback(
    (baseStyles: Record<string, React.CSSProperties> | undefined): ModalProps["styles"] =>
      ({
        ...baseStyles,
        container: { ...baseStyles?.container, ...elevatedBg },
        header: { ...baseStyles?.header, ...elevatedBg },
        body: { ...baseStyles?.body, ...elevatedBg },
      }) as ModalProps["styles"],
    [elevatedBg]
  );

  const isDanger = okType === "danger";
  const autoFocusEnabled = autoFocusFirstField ?? !isDanger;
  // Erst fokussieren, wenn der Inhalt steht — vorher gibt es kein Feld, das den Fokus verdient.
  const containerRef = useAutoFocusInContainer({ open: !!open, enabled: autoFocusEnabled && contentLoading !== true });

  const usesFormSubmit = !!formId && submitOnEnter;
  const effectiveConfirmLoading = submitting ?? confirmLoading;
  // Solange der Inhalt lädt, ist nichts abzuschicken — sperrt auch den Enter-Submit.
  const submitLocked = (okButtonProps?.disabled ?? false) || contentLoading === true;

  // Synchrones Doppelklick-/Doppel-Submit-Auffangnetz (Enter + OK-Klick), analog FormDrawer.
  const { submitBlocked, armSubmitLock } = useSubmitLock({
    open: !!open,
    loading: !!effectiveConfirmLoading,
    formId: usesFormSubmit ? formId : undefined,
    submitVisible: true,
    submitDisabled: submitLocked,
  });

  // onOk-Pfad (kein nativer Form-Submit): Riegel vor dem Aufruf prüfen, damit ein
  // zweiter Klick nicht erneut auslöst, bevor confirmLoading greift.
  const guardedOnOk = React.useCallback<NonNullable<ModalProps["onOk"]>>(
    (e) => {
      if (!onOk) return;
      if (!armSubmitLock()) return;
      return onOk(e);
    },
    [armSubmitLock, onOk]
  );

  const okLoading = submitBlocked || okButtonProps?.loading === true;
  const submitButtonProps = usesFormSubmit
    ? { ...okButtonProps, htmlType: "submit" as const, form: formId, loading: okLoading, disabled: submitLocked }
    : { ...okButtonProps, loading: okLoading, disabled: submitLocked };

  // Wenn Form drin (formId), schließt ein Mask-Klick NICHT — sonst gehen Eingaben verloren.
  // Aufrufer kann maskClosable explizit überschreiben.
  const resolvedMaskClosable = maskClosable ?? !formId;
  const resolvedMask = typeof mask === "object"
    ? { ...mask, closable: mask.closable ?? resolvedMaskClosable }
    : { enabled: mask ?? true, closable: resolvedMaskClosable };

  const renderWithRef = React.useCallback(
    (node: React.ReactNode) => {
      const wrapped = (
        <div ref={containerRef} style={{ display: "contents" }}>
          {node}
        </div>
      );
      return modalRender ? modalRender(wrapped) : wrapped;
    },
    [containerRef, modalRender]
  );

  const resolvedStyles = React.useMemo<ModalProps["styles"]>(() => {
    if (typeof styles === "function") {
      return ((info: Parameters<typeof styles>[0]) =>
        mergeElevatedStyles(styles(info) as Record<string, React.CSSProperties> | undefined)) as ModalProps["styles"];
    }
    return mergeElevatedStyles(styles as Record<string, React.CSSProperties> | undefined);
  }, [mergeElevatedStyles, styles]);

  return (
    <Modal
      {...props}
      open={open}
      okType={okType}
      onOk={guardedOnOk}
      okButtonProps={submitButtonProps}
      confirmLoading={effectiveConfirmLoading}
      modalRender={renderWithRef}
      mask={resolvedMask}
      destroyOnHidden={props.destroyOnHidden ?? destroyOnClose}
      styles={resolvedStyles}
    >
      {/*
        Nur wrappen, wenn der Aufrufer die Prop überhaupt benutzt: `LoadingOverlay` bringt eigene
        Container-Divs mit, und die dürfen nicht in jedem Modal des Produkts auftauchen — ein
        Body mit eigenem Flex-Layout bekäme sonst plötzlich ein einzelnes Kind.
      */}
      {contentLoading === undefined
        ? children
        : <LoadingOverlay visible={contentLoading}>{children}</LoadingOverlay>}
    </Modal>
  );
}
